import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { fromJS, List } from 'immutable';

import * as mainApprovalActions from '../actions/approval/main';
import * as associateApprovalActions from '../actions/approval/associate';
import { handleTargetSetNextActions } from '../actions/browse';
import { fetchTargetStatuses } from '../actions/statuses';
import { Approval } from '../components/Approval';
import config from '../config';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { getChanged, getIn, toJS } from '../utils/ChangeSpy';
import { showError } from '../utils/MessagePopup';
import { isResearcher } from '../utils/checkPermissions';
import { successWindwardGeneration } from '../utils/successWindwardGeneration';
import downloadFile from '../utils/downloadFile';
import { openInternalLink } from '../utils/url';
import { usePopupContext } from '../contexts/PopupContext';

const FIELDS = config.constants.getIn(['approval', 'associate', 'fields']);

function getSort(name, direction) {
  try {
    return `${direction === 'desc' ? '-' : ''}${FIELDS.filter(f => f.get('local') === name).toJS()[0].remote}`;
  } catch (error) {
    console.warn(error);
  }
}

/**
 * Approval container component.
 *
 * @param props {Object}.
 * @param project {Immutable.Map} Project information.
 * @param approvals {Immutable.List} List approval information.
 * @param associates {Immutable.List} List associates.
 * @param common {Immutable.Map} Common information.
 * @param preLoad {Function} Preload Page.
 * @param select {Function} Select an approval list.
 * @param change (Function) change date sent and returned.
 * @param clearError {Function} Clear errors.
 * @param sort {Function} Sort associate table.
 */
const ApprovalContainer = props => {
  const {
    user,
    common,
    project,
    associates,
    history,
    match,
    disableSetNA,
    disableChangeStatus,
    onFetchStatuses,
    loadProject,
    loadLinksList,
    loadAssociates,
    sort,
    selectApproval,
    changeDate,
    changeGrid,
    saveAssociate,
    setApprovedStatus,
    handleTargetSetNextActions,
    runWindwardReport,
    saveApproval,
    updateCompany,
    clearError,
  } = props;

  const { openPopup, closePopup, endPopupLoading } = usePopupContext();

  const [statuses, setStatuses] = useState(List());

  // AG-Grid's cellRendererFramework threads `onClick` through `colDef`, and doesn't reliably
  // re-bind cell renderers to a fresh `colDef.onClick` when only that callback's identity changes
  // (no other column-def field changes). Keeping handleClick's own identity permanently stable and
  // reading the latest values through refs (instead of the useCallback closure) sidesteps that gap -
  // this mirrors what a class component gets for free via `this.props` always being live.
  const commonRef = useRef(common);
  const projectRef = useRef(project);
  const associatesRef = useRef(associates);

  commonRef.current = common;
  projectRef.current = project;
  associatesRef.current = associates;

  const userHasAccess = user ? !isResearcher(user.getIn(['roles', 0, 'slug'], null)) : false;

  const showWarningPopup = useCallback(() => {
    showError(openPopup, config.permisionError, () => {
      history.push('/');
      closePopup();
    });
  }, [openPopup, closePopup, history]);

  useEffect(() => {
    const { projectId } = match.params;
    const approvalId = match.params.approvalId || match.params.approvalId;

    onFetchStatuses(({ response: { data } }) => {
      setStatuses(fromJS(data));
    });

    loadProject(projectId, approvalId, 1);
  }, []);

  const hasMountedRef = useRef(false);
  const previousCommonRef = useRef(common);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      previousCommonRef.current = common;

      return;
    }

    const previousCommon = previousCommonRef.current;

    previousCommonRef.current = common;

    const dateSent = toJS(common.getIn(['approval', 'dateSent']));
    const prevDateSent = toJS(previousCommon.getIn(['approval', 'dateSent']));

    const projectId = parseInt(match.params.projectId, 10);
    const approvalId = parseInt(match.params.approvalId, 10) || match.params.approvalId;

    if (dateSent !== prevDateSent) {
      loadLinksList(projectId, approvalId);
    }

    if (!userHasAccess) {
      showWarningPopup();
    } else {
      const errors = common.get('errors');
      const callback = () => clearError();

      if (errors.size > 0) {
        showError(openPopup, errors, callback);
      }
    }
  });

  const handleSort = useCallback(
    sortInfo => {
      const { projectId, approvalId } = match.params;

      if (sortInfo.length) {
        const sorting = getSort(sortInfo[0].colId, sortInfo[0].sort);

        sort(sorting);
        loadAssociates(projectId, approvalId, 1, sorting);
      } else {
        sort('');
        loadAssociates(projectId, approvalId, 1);
      }
    },
    [match, sort, loadAssociates],
  );

  const handleSelect = useCallback(
    approvalId => {
      const { projectId } = match.params;

      selectApproval(approvalId);
      sort('');
      loadAssociates(projectId, approvalId, 1);

      history.push(`/project/${projectId}/approval/${approvalId}`);
    },
    [match, selectApproval, sort, loadAssociates, history],
  );

  const handleChange = useCallback(
    ({ target: { name, value } }) => {
      changeDate(name, value);
    },
    [changeDate],
  );

  const handleGridChange = useCallback(
    ({ target: { value } }, i, field) => {
      changeGrid(i, field, value);
    },
    [changeGrid],
  );

  const handleGridSubmit = useCallback(
    (event, i, field) => {
      event.preventDefault();

      const targetId = associates.getIn([i, 'targetId']);
      const linkId = associates.getIn([i, 'linkId']);
      const { approvalId, projectId } = match.params;
      const body = {
        [field]: getIn(associates, [i, field]),
      };

      saveAssociate({ targetId, linkId, approvalId, projectId, body });
    },
    [associates, match, saveAssociate],
  );

  const handleUpdateNextActions = useCallback(
    (formData, callback) => {
      const { params } = match;
      const selectedList = associates.map(a => a.get('targetId')).toArray();
      const statusId = statuses.findKey(
        item => item.get('fc') === formData.getIn(['status', 'selected', 'value'], null),
      );

      const bodyParams = {
        selectedIds: selectedList,
        override: formData.get('override'),
        backlogged: formData.get('backlog', false),
        buyerId: project.get('buyerId'),
        statusId,
      };

      if (formData.get('date')) {
        bodyParams.date = formData.get('date').format('YYYY-MM-DD');
      }

      if (formData.getIn(['assignedTo', 'selected'])) {
        bodyParams.userId = formData.getIn(['assignedTo', 'selected', 'id']);
      }

      if (formData.getIn(['activity', 'selected'])) {
        bodyParams.activity = formData.getIn(['activity', 'selected', 'value']);
      }

      if (formData.get('description')) {
        bodyParams.description = formData.get('description');
      }
      ['approved', 'noResponse', 'passed'].forEach(key => {
        bodyParams[key] = false;
        if (formData.get(key)) {
          bodyParams[key] = true;
        }
      });

      const originalAfterSuccess = callback.afterSuccess;

      callback.afterSuccess = ({ response }) => {
        const approvalId = common.getIn(['approval', 'id']);
        const page = common.getIn(['paging', 'currentPage']);

        const requestListId = approvalId || (common.get('noApproval').size > 0 && 'no_approval_lists');

        selectApproval(requestListId);
        loadAssociates(params.projectId, requestListId, page, common.get('sorting'));
        originalAfterSuccess({ response });
      };

      handleTargetSetNextActions({
        body: {
          params: bodyParams,
        },
        callback,
      });
    },
    [match, associates, statuses, project, common, selectApproval, loadAssociates, handleTargetSetNextActions],
  );

  const handleSetAction = useCallback(() => {
    if (disableSetNA === false) {
      openPopup('SetNextActionsPopup', {
        currentPage: 'approval',
        onUpdate: handleUpdateNextActions,
      });
    }
  }, [disableSetNA, openPopup, handleUpdateNextActions]);

  /** Open modal window for request a Windward reports generation. */
  const handleRunWindwardReport = useCallback(() => {
    const selectedApprovalId = common.getIn(['approval', 'id']);

    openPopup('RunWindwardReportPopup', {
      callback: (selectedUserId, nameTemplate, templateParams) => {
        runWindwardReport({
          selectedApprovalId,
          selectedUserId,
          nameTemplate,
          templateParams,
          afterSuccess: ({ response: { data } }) =>
            successWindwardGeneration(data, { openPopup, closePopup, endPopupLoading }),
          afterError: () => endPopupLoading(),
        });
      },
    });
  }, [common, openPopup, closePopup, endPopupLoading, runWindwardReport]);

  /** Open modal Generate Online Approval List. */
  const handleGenerateOnlineApprovalList = useCallback(() => {
    const { params } = match;

    openPopup('GenerateOnlineApprovalListPopup', {
      callback: optionalFields => {
        const search = new URLSearchParams(
          optionalFields.map(field => [Object.keys(field)[0], Object.values(field)[0]]),
        );
        const searchString = search.toString();

        openInternalLink(`/project/${params.projectId}/approval/${params.approvalId}/preview?${searchString}`);
        closePopup();
      },
    });
  }, [match, openPopup, closePopup]);

  /**
   * Click on the approve / no approve icon handler.
   *
   * @param {number} targetBuyerId Id of the buyer target.
   * @param {boolean|""} approved Approved state.
   * @param {number} index Index.
   */
  const handleClick = useCallback(
    ({ targetBuyerId, approved, index }) => {
      const currentCommon = commonRef.current;
      const currentProject = projectRef.current;
      const currentAssociates = associatesRef.current;

      const approvalId =
        currentCommon.getIn(['approval', 'id']) || (currentCommon.get('noApproval') && 'no_approval_lists');
      const projectId = currentProject.get('id');
      const prevApprovedState = currentAssociates.toJS().filter(associate => associate.linkId === targetBuyerId)[0]
        .approved.value; // Get previous approved state before change it.

      setApprovedStatus({ targetBuyerId, approved, index, projectId, approvalId, prevApprovedState });
    },
    [setApprovedStatus],
  );

  const handleChangeStatus = useCallback(() => {
    const approvalId = common.getIn(['approval', 'id']);
    const projectId = project.get('id');

    const requestListId = approvalId || (common.get('noApproval').size > 0 && 'no_approval_lists');

    if (disableChangeStatus === false) {
      openPopup('ChangeStatusPopup', { approvalId: requestListId, projectId });
    }
  }, [common, project, disableChangeStatus, openPopup]);

  const handleExport = useCallback(() => {
    const projectId = project.get('id');
    const approvalId = common.getIn(['approval', 'id']);

    const listUrl = approvalId
      ? `approval_lists/${approvalId}`
      : common.get('noApproval').size > 0 && 'no_approval_lists';

    downloadFile({
      url: `${config.API_BASE_URL}/api/v1/projects/${projectId}/${listUrl}/export`,
    });
  }, [project, common]);

  const handleOpenLink = useCallback(({ data }) => {
    openInternalLink(`/company/${data.targetId}/${data.isTarget ? 'target' : 'buyer'}`);
  }, []);

  const handleLoadNext = useCallback(
    page => {
      const { params } = match;
      const projectId = parseInt(params.projectId, 10);
      const approvalId = parseInt(params.approvalId, 10) || params.approvalId;

      loadAssociates(projectId, approvalId, page, common.get('sorting'));
    },
    [match, common, loadAssociates],
  );

  const handleSaveDates = useCallback(() => {
    const projectId = project.get('id');
    const approvalId = common.getIn(['approval', 'id']);
    const body = getChanged(common.get('approval'));
    const bodyToSend = {};

    Object.keys(body).forEach(key => {
      if (/date/.test(key)) {
        try {
          if (key === 'dateReturned') {
            bodyToSend.dateReceived = body[key] && body[key].isValid() ? body[key].format('YYYY-MM-DD') : null;
          } else {
            bodyToSend[key] = body[key] && body[key].isValid() ? body[key].format('YYYY-MM-DD') : null;
          }
        } catch (error) {
          bodyToSend[key] = undefined;
        }
      }
    });
    saveApproval(projectId, approvalId, bodyToSend, body);
  }, [project, common, saveApproval]);

  const handleUpdateCompany = useCallback(
    (body, { companyId, buyerId, merge }) => {
      const postData = { ...body };

      if (postData.bsnClient === null) {
        postData.bsnClient = '';
      }

      if (postData.harveyComment === null) {
        postData.harveyComment = '';
      }

      updateCompany(postData, companyId, buyerId, merge);
    },
    [updateCompany],
  );

  const approvalLabel = common.getIn(['approval', 'label']);

  let pageTitle = '';

  if (approvalLabel) {
    pageTitle = approvalLabel;
  } else if (common.get('noApproval').size > 0) {
    pageTitle = '(No Approval List)';
  }

  const content = userHasAccess ? (
    <Approval
      {...props}
      onChange={handleChange}
      onChangeStatus={handleChangeStatus}
      onClick={handleClick}
      onExport={handleExport}
      onGenerateOnlineApprovalList={handleGenerateOnlineApprovalList}
      onGridChange={handleGridChange}
      onGridSubmit={handleGridSubmit}
      onLoad={handleLoadNext}
      onOpenLink={handleOpenLink}
      onRunWindwardReport={handleRunWindwardReport}
      onSaveDates={handleSaveDates}
      onSelect={handleSelect}
      onSetAction={handleSetAction}
      onSort={handleSort}
      onUpdateCompany={handleUpdateCompany}
    />
  ) : null;

  return (
    <div className="full-height">
      <Helmet title={`${pageTitle} [Approval Page]`.trim()} />
      {content}
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.approval,
  disableSetNA: state.approval.associates.size === 0,
  disableChangeStatus: state.approval.project.get('id') === 0,
  user: state.auth.get('user', null),
});

const mapDispatchToProps = dispatch => ({
  onFetchStatuses: callback => dispatch(fetchTargetStatuses(callback)),
  handleTargetSetNextActions: payload => dispatch(handleTargetSetNextActions(payload)),
  ...bindActionCreators(mainApprovalActions, dispatch),
  ...bindActionCreators(associateApprovalActions, dispatch),
});

export { ApprovalContainer };
export default connect(mapStateToProps, mapDispatchToProps, mergeProps, connectOptions)(memo(ApprovalContainer));
